import { type Box, Input } from '@zillow/constellation-10';
import { CustomCombobox } from '../../SharedComponents';
import { ALL_PROPERTIES, getAllPropertiesOptions } from './MainPageLayout.utils';
import { MainLayoutType, useMainLayout } from './MainLayoutContext';
import { useMemo, useState } from 'react';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { emitAnalyticsFilterChoosePropertyEvent } from '../../../analytics/events';
import type { ListingAlias } from '@zg-rentals/ts-customer-analytics-client';
import { formatAddress } from '../../../utils/formatAddress';

type CssProp = React.ComponentProps<typeof Box>['css'];

export const PropertiesSelect = ({ css = {} }: { css?: CssProp }) => {
  const { allProperties, selectedFilters, onSelectProperty, layout, visibleRegionIds } = useMainLayout();
  const { user } = useRentalManagerUser();
  const [search, setSearch] = useState<string | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  const allPropertiesOptions = useMemo(
    () =>
      allProperties.length > 0
        ? getAllPropertiesOptions(allProperties, search || '', layout === MainLayoutType.MAP, visibleRegionIds)
        : [],
    [allProperties, search, layout, visibleRegionIds],
  );

  const displayName = useMemo(() => {
    if (!selectedFilters.listingAlias) return ALL_PROPERTIES;
    const property = allProperties.find((p) => p.propertyDetails.listingAlias === selectedFilters.listingAlias);
    if (!property) return ALL_PROPERTIES;
    return property.propertyDetails.listingName || formatAddress(property.propertyDetails);
  }, [selectedFilters.listingAlias, allProperties]);

  if (allProperties.length === 0) return null;

  return (
    <CustomCombobox
      css={{ width: { base: '100%', md: '560px' }, marginRight: { base: '0', md: '600' }, ...css }}
      placeholder={ALL_PROPERTIES}
      renderClear={() => null}
      value={selectedFilters.listingAlias}
      onChange={(value: string) => {
        if (user) emitAnalyticsFilterChoosePropertyEvent({ user });
        onSelectProperty(value as ListingAlias | '', allProperties);
        setSearch(null);
        setOpen(false);
      }}
      open={open}
      onOpenChange={(nextOpen) => setOpen(nextOpen)}
      renderInput={({ onFocus, onBlur }) => (
        <Input
          value={search !== null ? search : displayName}
          onChange={(e) => {
            if (search === null && e.target.value === displayName) return;
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={ALL_PROPERTIES}
        />
      )}
      options={allPropertiesOptions}
    />
  );
};
